import React from 'react';
import { Box, Heading, Icon, Table, Text, Tooltip } from '@zillow/constellation-10';
import { numberUtils, useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import billingUtils from '../../utils/billingUtils';
import { StyledContainer } from './StyledComponents';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraBillingInvoiceItemDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';
import { IconCalendarOutline, IconQuestionMarkCircleFilled } from '@zillow/constellation-icons';

const mapActiveRanges = (activeRanges: string) => {
  if (!activeRanges) {
    return '-';
  }
  return activeRanges.split(';').map((range) => (
    <div key={range} className="InvoicePage-active-range">
      {range}
    </div>
  ));
};

export interface SfhBreakdownTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const SfhBreakdownTable = ({ currentInvoice }: SfhBreakdownTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();

  const isFlatFeeFeedConnect = currentInvoice
    ? billingUtils.isFlatFeeFeedConnectInvoice(currentInvoice.sfhLineItems)
    : false;
  const isSingleFamilyLegacy =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE);

  const chargeItemTooltipMessage = isFlatFeeFeedConnect
    ? 'Billed a flat rate per month at the account level regardless of number of listings posted.'
    : 'Billed per published unit per day.';

  const showUnitPrice = !(isSingleFamilyLegacy || isFlatFeeFeedConnect);
  const isSingleFamily =
    currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS ||
    currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
    currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE;
  const quantityHeader = isSingleFamily ? 'Active Days' : 'QTY';
  const unitPriceHeader =
    currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS ? 'Daily Rate' : 'Unit Price';
  const subtotalHeader = isSingleFamilyLegacy || isFlatFeeFeedConnect ? 'EST. SUBTOTAL' : 'SUBTOTAL';

  const breakdownsWithCharges = (currentInvoice?.sfhLineItems ?? []).filter((item) => item.chargeAmount !== 0);

  if (breakdownsWithCharges.length === 0) {
    return null;
  }

  const desktopAlignments: Array<'start' | 'end'> = ['start', 'start', 'end'];
  if (showUnitPrice) desktopAlignments.push('end');
  desktopAlignments.push('end', 'end', 'end');

  const renderQuantityCell = (item: AdproductsZuoraBillingInvoiceItemDto) => {
    const { quantity, activeRanges } = item;
    if (isNaN(quantity)) return '-';
    const formatted = quantity % 1 === 0 ? quantity : quantity.toFixed(2);
    if (item.chargeName.includes('Zillow Feed Connect')) {
      return (
        <Tooltip.Root useHoverProps={{ delay: 100, restMs: 100 }}>
          <Tooltip.Trigger>
            <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {formatted}
              <Icon size="md">
                <IconCalendarOutline />
              </Icon>
            </Box>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content>{mapActiveRanges(activeRanges)}</Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      );
    }
    return formatted;
  };

  return (
    <StyledContainer>
      <Heading level={4}>Listing breakdown</Heading>
      <Box css={{ margin: 'xs' }} />
      {isScreenDesktop ? (
        <Table.Root appearance="horizontal" aria-label="Listing breakdown table" alignments={desktopAlignments}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Property</Table.HeaderCell>
              <Table.HeaderCell>Charge Item</Table.HeaderCell>
              <Table.HeaderCell>{quantityHeader}</Table.HeaderCell>
              {showUnitPrice && <Table.HeaderCell>{unitPriceHeader}</Table.HeaderCell>}
              <Table.HeaderCell>{subtotalHeader}</Table.HeaderCell>
              <Table.HeaderCell>Tax</Table.HeaderCell>
              <Table.HeaderCell>Total amount due</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {breakdownsWithCharges.map((item) => (
              <Table.Row key={item.chargeId}>
                <Table.Cell>{item.propertyName}</Table.Cell>
                <Table.Cell>
                  {item.chargeName.includes('Zillow Feed Connect') ? (
                    <Tooltip
                      useHoverProps={{ delay: 100, restMs: 100 }}
                      content={chargeItemTooltipMessage}
                      trigger={
                        <Text textStyle="body-xs" css={{ display: 'flex', alignItems: 'center' }}>
                          {item.chargeName}
                          <Icon size="sm">
                            <IconQuestionMarkCircleFilled />
                          </Icon>
                        </Text>
                      }
                    />
                  ) : (
                    item.chargeName
                  )}
                </Table.Cell>
                <Table.Cell>{renderQuantityCell(item)}</Table.Cell>
                {showUnitPrice && (
                  <Table.Cell>
                    {!item.quantity ? (
                      '-'
                    ) : (
                      <span>
                        {numberUtils.asCurrencyWithDefault(item.chargeAmount / item.quantity, '-')}
                        {billingUtils.formatDiscountRate(item) && <div>{billingUtils.formatDiscountRate(item)}</div>}
                      </span>
                    )}
                  </Table.Cell>
                )}
                <Table.Cell>
                  <span>
                    {numberUtils.asCurrency(item.chargeAmount)}
                    {billingUtils.formatDiscountAmount(item) && <div>{billingUtils.formatDiscountAmount(item)}</div>}
                  </span>
                </Table.Cell>
                <Table.Cell>{numberUtils.asCurrency(item.taxAmount ?? 0)}</Table.Cell>
                <Table.Cell>{numberUtils.asCurrency(item.chargeAmount + (item.taxAmount ?? 0))}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      ) : (
        <Table.Root
          appearance="horizontal"
          aria-label="Listing breakdown table"
          alignments={['start', 'start', 'end', 'end']}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Property</Table.HeaderCell>
              <Table.HeaderCell>Product</Table.HeaderCell>
              <Table.HeaderCell>Tax</Table.HeaderCell>
              <Table.HeaderCell>Total amount due</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {breakdownsWithCharges.map((item) => (
              <Table.Row key={item.chargeId}>
                <Table.Cell>{item.propertyName}</Table.Cell>
                <Table.Cell>{item.chargeName}</Table.Cell>
                <Table.Cell>{numberUtils.asCurrency(item.taxAmount ?? 0)}</Table.Cell>
                <Table.Cell>{numberUtils.asCurrency(item.chargeAmount + (item.taxAmount ?? 0))}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      <Text textStyle="body-xs">Amount includes tax.</Text>
      <Box css={{ margin: 'xs' }} />
    </StyledContainer>
  );
};
